/**
 * Shared helpers for Company.info shareholder-graph / ultimate-shareholders APIs.
 * Used by ownership graph, subsidiaries list, ultimate shareholders, etc.
 */

export const RELATION_TYPE_LABELS = {
    is_partner_of: 'Partner',
    is_founder_of: 'Founder',
    is_german_partner_of: 'German partner',
    is_limited_partner_of: 'Limited partner',
    is_proprietor_of: 'Proprietor',
    is_share_owner_of: 'Share owner',
    is_unlimited_partner_of: 'Unlimited partner',
};

export const ENTITY_TYPE_LABELS = {
    companies: 'Company',
    persons: 'Person',
    departments: 'Department',
    groups: 'Group',
};

export const ENTITY_TYPE_NODE_TYPES = {
    companies: 'company',
    persons: 'person',
    departments: 'department',
    groups: 'group',
};

export const formatEntityTypeLabel = (entityType, t) => {
    if (!entityType) {
        return null;
    }

    const mappedLabel = ENTITY_TYPE_LABELS[entityType] || entityType;

    return typeof t === 'function' ? t(mappedLabel) : mappedLabel;
};

export const getEntityNodeType = (entityType) => {
    return ENTITY_TYPE_NODE_TYPES[entityType] || 'group';
};

const humanizeRelationType = (type) => {
    const cleaned = String(type)
        .replace(/^is_/, '')
        .replace(/_of$/, '')
        .replace(/_/g, ' ')
        .trim();

    if (!cleaned) {
        return null;
    }

    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export const formatRelationTypeLabel = (type, t) => {
    if (!type) {
        return null;
    }

    const mappedLabel = RELATION_TYPE_LABELS[type] || humanizeRelationType(type);

    if (!mappedLabel) {
        return null;
    }

    return typeof t === 'function' ? t(mappedLabel) : mappedLabel;
};

export const formatRelationEdgeLabel = (relation, t) => {
    const typeLabel = formatRelationTypeLabel(relation?.type, t);
    const hasPercentage = typeof relation?.percentage === 'number';
    const percentageLabel = hasPercentage
        ? `${relation.percentage.toFixed(2)}%`
        : null;

    if (typeLabel && percentageLabel) {
        return `${typeLabel} · ${percentageLabel}`;
    }

    return typeLabel || percentageLabel || '';
};

/**
 * Subsidiaries = entities the main company points to
 * (relations where from === from_cin), same rule as the graph layout.
 */
export const extractSubsidiaries = (graphData) => {
    if (!graphData?.entities?.length || !graphData?.relations?.length || !graphData?.from_cin) {
        return [];
    }

    const mainId = graphData.from_cin;
    const entityMap = Object.fromEntries(
        graphData.entities.map((entity) => [entity.cin, entity])
    );

    return graphData.relations
        .filter((relation) => relation.from === mainId)
        .map((relation, index) => {
            const entity = entityMap[relation.to] || {};
            const info = entity.additional_information || {};
            const address = [
                info.address_1,
                info.city,
                info.country,
                info.postcode,
            ]
                .filter(Boolean)
                .join(', ');

            return {
                id: `${relation.to}-${relation.type || 'relation'}-${index}`,
                cin: entity.cin || relation.to,
                name: entity.name || '—',
                entityType: entity.type || null,
                relationType: relation.type || null,
                percentage:
                    typeof relation.percentage === 'number'
                        ? relation.percentage
                        : null,
                address: address || null,
                country: info.country || null,
                city: info.city || null,
            };
        });
};

const formatShareholderAddress = (address) => {
    if (!address) {
        return null;
    }

    if (typeof address === 'string') {
        return address;
    }

    const formatted = [address.city, address.country].filter(Boolean).join(', ');
    return formatted || null;
};

/**
 * Individual shareholders from ultimate-shareholder API (`type === persons`).
 */
export const extractIndividualShareholders = (ultimateShareholdersData) => {
    if (!Array.isArray(ultimateShareholdersData?.shareholders)) {
        return [];
    }

    return ultimateShareholdersData.shareholders
        .filter((shareholder) => shareholder.type === 'persons')
        .map((shareholder, index) => {
            const percentageValue =
                typeof shareholder.calculated_percentage === 'number'
                    ? shareholder.calculated_percentage
                    : null;

            return {
                id: shareholder.cin || `person-${index}`,
                cin: shareholder.cin || null,
                fullName: shareholder.name || '—',
                type: shareholder.type,
                percentage:
                    percentageValue !== null ? percentageValue.toFixed(2) : null,
                isUBO: percentageValue !== null ? percentageValue >= 15 : false,
                address: formatShareholderAddress(shareholder.address),
                hasFindings: !!shareholder.hasFindings,
                underMonitoring: !!shareholder.has_monitoring_follow_list_item,
                complete: shareholder.complete,
                maxDepth: shareholder.max_depth,
                minDepth: shareholder.min_depth,
                raw: shareholder,
            };
        });
};
